import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  ShieldCheck, Users, Zap, MessageCircle, MapPin, ArrowRight, Star, ChevronLeft, ChevronRight, Quote,
} from "lucide-react";
import { API, STORE, CATEGORIES, HERO_SLIDES, TESTIMONIALS, waLink, resolveImg, OFFER } from "@/lib/store";
import { BikeCard } from "@/components/BikeCard";

const TRUST = [
  { icon: Users, title: "Wide Range for All Ages", text: "From toddlers to adults — kids, BMX, mountain, road & electric bikes." },
  { icon: ShieldCheck, title: "Free Helmet & Pump", text: "Every single bike purchase includes a safety helmet and bike pump — free." },
  { icon: Zap, title: "Trusted Local Experts", text: "Honest advice and quality bikes from a shop Lagos riders trust." },
  { icon: MessageCircle, title: "Fast WhatsApp Response", text: "Enquire any time — we reply quickly with availability and pickup info." },
];

const Hero = () => {
  const [index, setIndex] = useState(0);
  const timer = useRef(null);

  useEffect(() => {
    timer.current = setInterval(() => setIndex((i) => (i + 1) % HERO_SLIDES.length), 5000);
    return () => clearInterval(timer.current);
  }, []);

  const go = (i) => {
    setIndex((i + HERO_SLIDES.length) % HERO_SLIDES.length);
    if (timer.current) clearInterval(timer.current);
    timer.current = setInterval(() => setIndex((x) => (x + 1) % HERO_SLIDES.length), 5000);
  };

  return (
    <section data-testid="hero-carousel" className="relative h-[88vh] min-h-[560px] w-full overflow-hidden bg-[#0B0A09]">
      {HERO_SLIDES.map((s, i) => (
        <div
          key={i}
          className="absolute inset-0 transition-opacity duration-[1200ms] ease-in-out"
          style={{ opacity: i === index ? 1 : 0 }}
          aria-hidden={i !== index}
        >
          <img src={s.image} alt={s.title} className="h-full w-full object-cover" loading={i === 0 ? "eager" : "lazy"} />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0B0A09]/90 via-[#0B0A09]/60 to-[#0B0A09]/30" />
        </div>
      ))}

      <div className="relative z-10 mx-auto flex h-full max-w-7xl items-center px-4 sm:px-6 lg:px-8">
        <div key={index} className="kn-fade-up max-w-2xl">
          <span className="kn-overline text-[#e0b48a]">{STORE.motto}</span>
          <h1 className="mt-4 font-display text-4xl font-extrabold leading-[1.05] text-white sm:text-5xl lg:text-6xl">
            {HERO_SLIDES[index].title}
          </h1>
          <p className="mt-5 max-w-xl text-lg text-zinc-200">{HERO_SLIDES[index].subtitle}</p>
          <div className="mt-8 flex flex-col gap-3 sm:flex-row">
            <a
              data-testid="hero-whatsapp-cta"
              href={waLink(`Hello ${STORE.name}, I'd like to enquire about your bikes. ${STORE.motto}!`)}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-wa inline-flex items-center justify-center gap-2 rounded-full px-7 py-4 text-base font-bold"
            >
              <MessageCircle className="h-5 w-5" /> Chat on WhatsApp
            </a>
            <Link
              to="/bikes"
              className="inline-flex items-center justify-center gap-2 rounded-full border border-white/30 bg-white/5 px-7 py-4 text-base font-bold text-white backdrop-blur transition-colors hover:bg-white/15"
            >
              Browse All Bikes <ArrowRight className="h-5 w-5" />
            </Link>
          </div>
        </div>
      </div>

      <div className="absolute bottom-6 left-0 right-0 z-10 flex items-center justify-center gap-3">
        {HERO_SLIDES.map((_, i) => (
          <button
            key={i}
            onClick={() => go(i)}
            aria-label={`Go to slide ${i + 1}`}
            className={`h-2 rounded-full transition-all ${i === index ? "w-8 bg-[#8C5A32]" : "w-2 bg-white/50 hover:bg-white/80"}`}
          />
        ))}
      </div>
      <button onClick={() => go(index - 1)} aria-label="Previous slide" className="absolute left-4 top-1/2 z-10 hidden -translate-y-1/2 rounded-full border border-white/20 bg-black/30 p-2.5 text-white backdrop-blur transition hover:bg-black/60 md:block">
        <ChevronLeft className="h-5 w-5" />
      </button>
      <button onClick={() => go(index + 1)} aria-label="Next slide" className="absolute right-4 top-1/2 z-10 hidden -translate-y-1/2 rounded-full border border-white/20 bg-black/30 p-2.5 text-white backdrop-blur transition hover:bg-black/60 md:block">
        <ChevronRight className="h-5 w-5" />
      </button>
    </section>
  );
};

const SectionHead = ({ overline, title, subtitle, center }) => (
  <div className={`max-w-2xl ${center ? "mx-auto text-center" : ""}`}>
    <span className="kn-overline">{overline}</span>
    <h2 className="mt-3 font-display text-3xl font-bold tracking-tight text-[#0B0A09] sm:text-4xl">{title}</h2>
    {subtitle && <p className="mt-3 text-base text-zinc-500">{subtitle}</p>}
  </div>
);

export default function Home() {
  const [bikes, setBikes] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [testimonials, setTestimonials] = useState(TESTIMONIALS);
  const [activeCat, setActiveCat] = useState("all");

  useEffect(() => {
    document.title = "Knoble Rides Enterprises | Bicycle Store Lagos Nigeria — Bikes for All Ages";
    axios.get(`${API}/bikes`).then((r) => setBikes(r.data)).catch(() => {});
    axios.get(`${API}/gallery`).then((r) => setGallery(r.data.slice(0, 6))).catch(() => {});
    axios.get(`${API}/testimonials`).then((r) => { if (r.data.length) setTestimonials(r.data); }).catch(() => {});
  }, []);

  const filtered = activeCat === "all" ? bikes : bikes.filter((b) => b.category === activeCat);
  const tabs = [{ id: "all", name: "All" }, ...CATEGORIES.map((c) => ({ id: c.id, name: c.name.replace(" Bikes", "").replace("Bike ", "") }))];

  return (
    <div>
      <Hero />

      {/* Shop by Category */}
      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8">
        <SectionHead overline="Find Your Ride" title="Shop by Category" subtitle="Explore our full range of bicycles and gear for every kind of rider." />
        <div className="mt-10 grid grid-cols-2 gap-4 sm:gap-6 lg:grid-cols-3">
          {CATEGORIES.map((cat) => (
            <Link
              key={cat.id}
              to={`/bikes?category=${cat.id}`}
              data-testid={`category-card-${cat.id}`}
              className="card-lift group relative overflow-hidden rounded-2xl border border-[#E6D7C8] bg-[#0B0A09]"
            >
              <div className="aspect-[4/3] overflow-hidden">
                <img src={cat.image} alt={`${cat.name} for sale at Knoble Rides Lagos Nigeria`} loading="lazy" className="h-full w-full object-cover opacity-80 transition-all duration-500 group-hover:scale-110 group-hover:opacity-90" />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-[#0B0A09] via-[#0B0A09]/40 to-transparent" />
              <div className="absolute inset-x-0 bottom-0 p-5">
                <h3 className="font-display text-lg font-bold text-white sm:text-xl">{cat.name}</h3>
                <p className="mt-1 hidden text-sm text-zinc-300 sm:block">{cat.tagline}</p>
                <span className="mt-2 inline-flex items-center gap-1 text-sm font-semibold text-[#e0b48a]">
                  Browse <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                </span>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Available Bikes */}
      <section className="bg-white py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
            <SectionHead overline="Staff Picks" title="Available Bikes" subtitle="Handpicked bicycles ready to ride. Enquire on WhatsApp for the best price." />
            <Link to="/bikes" className="hidden shrink-0 items-center gap-1.5 font-semibold text-[#8C5A32] hover:text-[#734725] md:inline-flex">
              View full catalog <ArrowRight className="h-4 w-4" />
            </Link>
          </div>

          <div className="no-scrollbar mt-8 flex gap-2 overflow-x-auto pb-2">
            {tabs.map((t) => (
              <button
                key={t.id}
                onClick={() => setActiveCat(t.id)}
                className={`shrink-0 rounded-full px-4 py-2 text-sm font-semibold transition-colors ${
                  activeCat === t.id ? "bg-[#8C5A32] text-white" : "bg-[#F1ECE6] text-zinc-600 hover:bg-[#E6D7C8]"
                }`}
              >
                {t.name}
              </button>
            ))}
          </div>

          <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {filtered.slice(0, 6).map((b) => <BikeCard key={b.id} bike={b} />)}
          </div>
          {filtered.length === 0 && <p className="mt-8 text-center text-zinc-500">No bikes in this category yet.</p>}

          <div className="mt-10 text-center md:hidden">
            <Link to="/bikes" className="btn-brown inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-semibold">
              View full catalog <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Trust strip */}
      <section className="bg-[#0B0A09] py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <SectionHead overline="Why Knoble Rides" title="Ride With Confidence" center />
          <div className="mt-12 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {TRUST.map((t, i) => (
              <div key={i} className="rounded-2xl border border-white/10 bg-white/5 p-7 text-center transition-colors hover:border-[#8C5A32]/50">
                <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-[#8C5A32]">
                  <t.icon className="h-7 w-7 text-white" />
                </div>
                <h3 className="mt-5 font-display text-lg font-bold text-white">{t.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-zinc-400">{t.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="overflow-hidden py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <SectionHead overline="Let Customers Speak" title="Loved by Riders Across Lagos" center />
        </div>
        <div className="relative mt-12">
          <div className="flex w-max kn-marquee gap-6 px-3">
            {[...testimonials, ...testimonials].map((t, i) => (
              <figure key={i} className="w-[320px] shrink-0 rounded-2xl border border-[#E6D7C8] bg-white p-6 sm:w-[380px]">
                <Quote className="h-7 w-7 text-[#8C5A32]" />
                <div className="mt-3 flex gap-0.5">
                  {[...Array(t.rating || 5)].map((_, s) => <Star key={s} className="h-4 w-4 fill-[#8C5A32] text-[#8C5A32]" />)}
                </div>
                <blockquote className="mt-3 text-sm leading-relaxed text-zinc-700">"{t.quote}"</blockquote>
                <figcaption className="mt-4 text-sm font-bold text-[#0B0A09]">
                  {t.name} <span className="font-normal text-zinc-500">— {t.location}</span>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery preview */}
      <section className="bg-white py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
            <SectionHead overline="Our Store" title="Gallery Preview" subtitle="Real photos of our bikes and store in Mangoro, Lagos." />
            <Link to="/gallery" className="hidden shrink-0 items-center gap-1.5 font-semibold text-[#8C5A32] hover:text-[#734725] md:inline-flex">
              View full gallery <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="mt-8 grid grid-cols-2 gap-3 sm:gap-4 md:grid-cols-3">
            {gallery.map((g, i) => (
              <div key={g.id} className={`overflow-hidden rounded-xl bg-[#F1ECE6] ${i === 0 ? "col-span-2 row-span-2 md:col-span-2 md:row-span-2" : ""}`}>
                <img src={resolveImg(g.image_url)} alt={`Knoble Rides bikes and store Mangoro Lagos ${i + 1}`} loading="lazy" className="h-full w-full object-cover transition-transform duration-500 hover:scale-105" style={{ aspectRatio: i === 0 ? "1/1" : "1/1" }} />
              </div>
            ))}
          </div>
          <div className="mt-8 text-center md:hidden">
            <Link to="/gallery" className="btn-brown inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-semibold">
              View full gallery <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Location */}
      <section className="bg-[#FDF8F3] py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid items-center gap-10 lg:grid-cols-2">
            <div>
              <SectionHead overline="Visit Us" title="Find Us in Mangoro, Lagos" />
              <div className="mt-6 flex items-start gap-4 rounded-2xl border border-[#E6D7C8] bg-white p-6">
                <MapPin className="mt-1 h-6 w-6 shrink-0 text-[#8C5A32]" />
                <div>
                  <p className="font-display text-lg font-bold text-[#0B0A09]">{STORE.name}</p>
                  <p className="mt-1 text-zinc-600">{STORE.address}</p>
                  <div className="mt-4 flex flex-wrap gap-3">
                    <a
                      data-testid="location-get-directions"
                      href={STORE.directionsUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn-brown inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold"
                    >
                      <MapPin className="h-4 w-4" /> Get Directions
                    </a>
                    <a href={STORE.phoneTel} className="inline-flex items-center gap-2 rounded-full border border-[#E6D7C8] px-5 py-2.5 text-sm font-semibold text-[#0B0A09] hover:bg-[#FDF8F3]">
                      Call {STORE.phoneDisplay}
                    </a>
                  </div>
                </div>
              </div>
            </div>
            <div className="overflow-hidden rounded-2xl border border-[#E6D7C8] shadow-sm">
              <iframe title="Knoble Rides location map" src={STORE.mapEmbedUrl} className="h-[340px] w-full" loading="lazy" referrerPolicy="no-referrer-when-downgrade" />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
